import './index.scss';
import React, {memo, PropsWithChildren, useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {PasswordContainer} from '../../../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../../../components/Settings/SettingsPageContainer';
import {useRPC} from '../../../../../../hooks/rpc';
import {MenuEntity} from '../../../../../../store/control/state';
import {ColumnModel, SettingsList} from '../../../../../../components/Settings/SettingsList';
import {UpDown} from "../../components/UpDown";
import {useSortBySelector} from '../../../../../../hooks/sort-by-selector';
import { Configs } from '../../../../../../configs';
import {useLocalization} from "../../../../../../hooks/localization";
import {changeBusyAction} from "../../../../../../store/ui/actions";
import {useDispatch} from 'react-redux';
import {CheckBox} from "../../../../../../components/Checkbox";
import {hasAuthority} from "../../../../../../utils/role";

const keyExtractor = (item: MenuEntity) => item.id;


type MenuElementIconProps = {
    icon: string;
}

const MenuElementIcon = memo(function MenuElementIcon({icon}: MenuElementIconProps) {
        return (
            <div className={'menu-element-icon'}>
                <img src={icon} alt=''/>
            </div>
        );
    },
);

const ColorfulText = memo(function (props: PropsWithChildren<{}>) {
    return (
        <span className="color-red">{props.children}</span>
    );
});

export const LeftMenuListScreen = memo(function HomepageListScreen() {
    const {t} = useLocalization();
    const menuItemList = useSortBySelector(state => state.control.menuList, a => a.order);
    const dispatch = useDispatch()
    const [rpc] = useRPC();

    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuEntity>(null);
    const role = useSelector(state => state.ui.role);

    const buttons = useMemo(() => {
        if (!hasAuthority(role, "service")) return [];
        return [
            {
                text: t('edit_content'),
                url: `/settings/left-menu/${selectedMenuItem ? selectedMenuItem.id : ''}/contents/`,
                className: 'blue',
                hidden: !selectedMenuItem || selectedMenuItem.is_special,
            },
            {
                text: t('edit'),
                url: `/settings/left-menu/edit/${selectedMenuItem ? selectedMenuItem.id : ''}`,
                className: 'orange',
                hidden: !selectedMenuItem || selectedMenuItem.is_special,
            },
            {
                text: t('add'),
                url: '/settings/left-menu/edit/',
                className: 'green',
            },
        ]
    }, [t, selectedMenuItem, role]);

    const swapItemOrders = useCallback((itemA: MenuEntity, itemB: MenuEntity) => {
        dispatch(changeBusyAction(true))
        Promise.all([
            rpc.updateMenu({...itemA, order: itemB.order}),
            rpc.updateMenu({...itemB, order: itemA.order}),
        ]).then(() => {
            setSelectedMenuItem(null);
            dispatch(changeBusyAction(false));
        });
    }, [rpc, dispatch]);

    const switchVisibility = useCallback((item) => {
        rpc.updateMenu({
            ...item, is_hidden: !item.is_hidden
        }).then(r => console.log(r));
    }, [rpc])

    const onUpClick = useCallback((index: number) => {
        swapItemOrders(menuItemList[index - 1], menuItemList[index]);
    }, [swapItemOrders, menuItemList]);

    const onDownClick = useCallback((index: number) => {
        swapItemOrders(menuItemList[index + 1], menuItemList[index]);
    }, [swapItemOrders, menuItemList]);

    const menuIconList = useSelector(state => state.control.menuIconList);

    const displayNameExtractor = useCallback((item: MenuEntity, column: ColumnModel, index?: number) => {
        if (column.key === 'order') {
            return <UpDown
                index={index}
                onUpClick={index === 0 ? undefined : onUpClick}
                onDownClick={(index === menuItemList.length - 1) ? undefined : onDownClick}/>;
        }
        if (column.key === 'icon') {
            const found = (menuIconList || []).find((i) => i.icon === item.icon) || null;
            const prefix = Configs.IMAGE_PREFIX.replace(/\/$/, '');
            return found ? <MenuElementIcon icon={prefix + found.url}/> : '-';
        }

        if (column.key === 'name') {
            const getName = () => {
                switch (item.id) {
                    case 'area':
                        return t('left_menu_title.floors_rooms');
                    case 'intercom':
                        return t('left_menu_title.intercom');
                    case 'alarm':
                        return t('left_menu_title.alarm');
                    case 'scenario':
                        return t('left_menu_title.scenario');
                    case 'sensor':
                        return t('left_menu_title.sensors');
                    case 'camera':
                        return t('left_menu_title.camera');
                    case 'media-remote':
                        return t('left_menu_title.media');
                    case 'concierge':
                        return t('left_menu_title.concierge');
                    case 'settings':
                        return t('left_menu_title.settings');
                    case 'all-controls':
                        return t('left_menu_title.all_controls');
                    case 'energy':
                        return t('left_menu_title.energy');
                    default:
                        return item.name;
                }
            };

            const finalName = getName();

            if (item.is_special) {
                return <ColorfulText>{finalName}</ColorfulText>;
            }

            return finalName;
        }

        if (column.key === 'is_hidden') {
           if (['settings', 'alarm'].indexOf(item.id) == -1) {
               return <CheckBox value={item.is_hidden} set={() => {
                   switchVisibility(item)
               }}/>
           }
           return <></>
        }
        return item[column.key] || '-';
    }, [onUpClick, menuItemList.length, onDownClick, menuIconList, switchVisibility, t]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 2,
            name: t('name'),
            key: 'name',
        },
        {
            length: 2,
            name: t('icon'),
            key: 'icon',
        },
        {
            length: 2,
            name: t('order'),
            key: 'order',
        },
        {
            length: 1,
            name: t('is_hidden'),
            key: 'is_hidden',
        },
    ], [t]);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                backUrl='/settings/custom-menu'>
                <SettingsList
                    columns={columns}
                    items={menuItemList}
                    listType="leftMenuList"
                    selectedItem={selectedMenuItem}
                    onItemSelected={setSelectedMenuItem}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    sortDisabled={true}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
